import {ReplaySubject, combineLatest} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {gt, minus, plus, numUtil, times} from '@/helper/num';
import {currenciesCurrencyCache$, inAssetCurrencies$} from '@/config';
import {calcMoneyData$} from '@/calc/fiat';
import {isUtaSpot$} from '@/user/user';
import {spotWalletsCache$} from './spot-wallets-cache';
import {allSubWalletsCache$} from './sub-users-all/sub-users-assets';
import type {ws, SpotWalletsData, CalcMoneyData, CurrencyEntity, SpotWallet} from '@/types';

type WalletRaw = ws.WalletRaw;

// 包含inAssetsDisplay 为1和0的资产
const allSpotWalletData$ = new ReplaySubject<SpotWallet[]>(1);

// 有效钱包 inAssetsDisplay为1
export const spotWalletData$ = new ReplaySubject<SpotWalletsData>(1);

// 无效钱包 inAssetsDisplay为0
export const spotInvalidWalletData$ = new ReplaySubject<SpotWalletsData>(1);

combineLatest([allSpotWalletData$, inAssetCurrencies$])
  .pipe(
    map(([allSpotWalletData, inAssetCurrencies]) => {
      const spotWalletData = allSpotWalletData.filter(({currency}) => inAssetCurrencies.includes(currency));
      const spotInvalidWalletData = allSpotWalletData.filter(({currency}) => !inAssetCurrencies.includes(currency));
      return [spotWalletData, spotInvalidWalletData];
    })
  )
  .subscribe(([spotWalletData, spotInvalidWalletData]) => {
    spotWalletData$.next({wallets: spotWalletData});
    spotInvalidWalletData$.next({wallets: spotInvalidWalletData});
  });

combineLatest([isUtaSpot$, spotWalletsCache$, currenciesCurrencyCache$, calcMoneyData$, allSubWalletsCache$])
  .pipe(
    filter(([isUtaSpot]) => !isUtaSpot),
    map(convertWallets)
  )
  .subscribe((wallets) => allSpotWalletData$.next(wallets));

function convertWallets([isUtaSpot, cache, currencies, calcMoneyData, allSubWalletsCache]: [
  boolean,
  Map<string, WalletRaw>,
  Map<string, CurrencyEntity>,
  CalcMoneyData,
  Map<string, number>
]): SpotWallet[] {
  if (isUtaSpot) {
    return [];
  }
  return cache
    .toArray((wallet: WalletRaw) => convertWallet(wallet, currencies, calcMoneyData, allSubWalletsCache))
    .filter(Boolean)
    .sort((a, b) => (gt(b.totalBalanceValue, a.totalBalanceValue) ? 1 : -1));
}

function convertWallet(
  {lastUpdateTimeNs, currency, balanceEv, lockedTradingBalanceEv, lockedWithdrawEv},
  currenciesCurrencyCache: Map<string, CurrencyEntity>,
  calcMoneyData: CalcMoneyData,
  allSubWalletsCache
): SpotWallet {
  const currencyInfo = currenciesCurrencyCache.get(currency);
  if (!currencyInfo) {
    return null;
  }

  const {
    valueScale,
    valuePrecision = 8,
    valueFactor,
    name,
    displayCurrency,
    depositOpen,
    withdrawOpen,
    status,
  } = currencyInfo;
  // trading bot 资产是无缩放的，将bot资产按照钱包倍数放大，统一按照钱包逻辑处理
  const tradingBotBalance = allSubWalletsCache.has(currency) ? allSubWalletsCache.get(currency) : 0;
  const tradingBotEv = Number(times(tradingBotBalance, valueFactor));
  const tradingBotsStr = numUtil.delimit(numUtil.floor(tradingBotEv, valuePrecision, valueScale));
  const totalBalanceEv = Number(plus(balanceEv, tradingBotEv));
  const freezeBalanceEv = Number(plus(plus(lockedTradingBalanceEv, lockedWithdrawEv), tradingBotEv));
  const availableBalanceEv = Number(minus(totalBalanceEv, freezeBalanceEv));
  const availableBalance = calcBalance(availableBalanceEv, valuePrecision, valueScale);
  const availableBalance2 = calcBalance(availableBalanceEv, 2, valueScale);
  const availableBalance4 = calcBalance(availableBalanceEv, 4, valueScale);
  const [availableBalanceMoneyWU] = calcMoneyData(displayCurrency, availableBalanceEv);
  const [freezeBalanceMoneyWU] = calcMoneyData(displayCurrency, freezeBalanceEv);
  const [totalBalanceMoneyWU, totalBalanceValue] = calcMoneyData(displayCurrency, totalBalanceEv);
  return {
    time: Math.floor(lastUpdateTimeNs / 1e6),
    currency,
    displayCurrency,
    name,
    totalBalanceEv,
    totalBalance: calcBalance(totalBalanceEv, valuePrecision, valueScale),
    totalBalanceMoneyWU,
    totalBalanceValue,
    availableBalanceEv,
    availableBalance,
    availableBalanceWU: availableBalance + ' ' + currency,
    availableBalance2,
    availableBalance2WU: availableBalance2 + ' ' + currency,
    availableBalance4,
    availableBalance4WU: availableBalance4 + ' ' + currency,
    availableBalanceMoneyWU,
    freezeBalanceEv,
    freezeBalance: calcBalance(freezeBalanceEv, valuePrecision, valueScale),
    freezeBalanceMoneyWU,
    lockedTradingBalanceEv,
    lockedTradingBalance: calcBalance(lockedTradingBalanceEv, valuePrecision, valueScale),
    lockedWithdrawEv,
    lockedWithdraw: calcBalance(lockedWithdrawEv, valuePrecision, valueScale),
    depositOpen,
    withdrawOpen,
    status,
    tradingBotEv,
    tradingBotsStr,
  };
}

function calcBalance(balanceEv, valuePrecision, valueScale): string {
  return numUtil.delimit(numUtil.floor(balanceEv, valuePrecision, valueScale));
}
